import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import PayrollLinesInterface from '@app/Interface/Report/PayrollLinesInterface';
import { Button, Card, CardBody, Flex, FlexItem, PageSection } from '@patternfly/react-core';
import { AngleLeftIcon, DownloadIcon } from '@patternfly/react-icons';
import ItemLoader from '@app/Components/ItemLoader';

interface PayrollDetailsReportProps {
    pageId: number;
    payrollId: number;
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    clearSelectedPayroll: Function;
}

const PayrollDetailsReport: React.FC<PayrollDetailsReportProps> = ({ pageId, payrollId, setLastUpdatedText, clearSelectedPayroll }) => {

    const { userState } = React.useContext(UserContext);

    const [loading, setLoading] = React.useState(false);
    const [payrollLines, setPayrollLines] = React.useState<PayrollLinesInterface[]>([]);

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'AP_MPIS_TRANSFER_LINES')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        async function fetchPayrollLines() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await ReportDataHelper.getPayrollDetailsData(controller, userState.user.currentFpuId, pageId, payrollId);
                if (mounted) {
                    setPayrollLines(rows);
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setPayrollLines([]);
                    setLoading(false);
                }
            }
        }

        setLoading(true);
        fetchPayrollLines();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, payrollId, userState.user.id, userState.user.currentFpuId]);

    if (loading) {
        return <ItemLoader />;
    }

    if (payrollLines.length === 0) {
        return <p><em>No data to show for the selected payroll id</em></p>;
    }

    const pdfLink = ReportDataHelper.getReportPdfUrl(
        'payroll',
        userState.user.currentFpuId,
        undefined,
        payrollId
    );

    return (
        <>
            <Button
                onClick={e => clearSelectedPayroll()}
                icon={<AngleLeftIcon />}
            >
                Return to Payroll List
            </Button>
            <Flex>
                <FlexItem align={{ default: 'alignRight' }}>
                    <Button className="pf-u-float-right" icon={<DownloadIcon />} component="a" target="_blank" href={pdfLink}>
                        Download PDF
                    </Button>
                </FlexItem>
            </Flex>
            <PageSection hasOverflowScroll>
                <Card style={{ minWidth: 'fit-content' }}>
                    <CardBody>
                        <pre style={{ whiteSpace: 'nowrap' }}>
                            {payrollLines.map((payrollLine, index) =>
                                <div style={{ whiteSpace: 'pre' }} key={index} dangerouslySetInnerHTML={{ __html: payrollLine.description1.replace(/^"(.*)"$/, '$1') }} />
                            )}
                        </pre>
                    </CardBody>
                </Card>
            </PageSection>
        </>
    );
};

export default PayrollDetailsReport;